
import { CrudService } from "../../../core/services/crudService";
import { Settings } from "../../../core/services/configService";
import Component from "vue-class-component";
import { DateHelper } from "../../../core/helpers/dateHelper";
import store from "../../../core/store";
import PopularPosts from "../../../components/popularPosts/popularPosts.vue";
import { CustomerHelper } from "../../helpers/customerHelper";
import LoginForm from "../../../components/login/loginForm.vue";
import RegisterForm from "../../../components/login/registerForm.vue";
import Popup from "../../../components/popup/popup.vue";
import Popupv2 from "../../../components/popup/popup-v2.vue";
import { LocalStorage } from "../../../core/localStorage";
import Vue from "vue";
import { WeekDays } from "./campDays.vue";
import InputDate from "@shared/components/form/inputDate.vue";
import SubmitButton from "@shared/components/submitButton/submitButton.vue";
import CheckBox from "@shared/components/form/checkBox.vue";
import { Product } from "@shared/core/interfaces/product";
import { ShopService } from "@shared/core/services/shopService";
import { ParentNameTeam } from "@shared/crm/constants";
import CampTrainings from "./campTrainings.vue";

export interface Camp extends WeekDays {
  id: number;
  name: string;
  start: string;
  startStr: string; // formatted
  end: string;
  endStr: string; // formatted
  logo: string;
  logoImg: string;
  details: string;
  allowOnlineInscription: boolean;
  inscriptionStart: string;
  inscriptionStartStr: string;
  inscriptionEnd: string;
  inscriptionEndStr: string;
  allowOnlineOpenClassInscription: boolean;
  anticipationDaysToOpenClassInscription: number;
  product: Product;
}

@Component({
  components: {
    PopularPosts, LoginForm,
    RegisterForm, Popup,
    InputDate, CheckBox,
    SubmitButton, CampTrainings,
    Popupv2
  },
})
export default class CampPage extends Vue {
  private camp: Camp | null = null;
  private error: string = "";
  private ok: string = "";
  private showInscriptionForm: boolean = false;
  private showRegister: boolean = false;
  private showLogin: boolean = false;
  private showLoginRegister: boolean = false;
  private customerId: number | null = null;
  private showPopupCopiedLink: boolean = false;
  private successMsg: string = "";

  // teamId
  private get campId() {
    return Number(this.$route.params.id);
  }

  private get availableInscriptionDays() {
    const inscriptionDays: { field: string, label: string }[] = [];
    if (this.camp) {
      if (this.camp?.mon) inscriptionDays.push({ field: "mon", label: this.$t("Monday").toString() });
      if (this.camp?.tue) inscriptionDays.push({ field: "tue", label: this.$t("Tuesday").toString() });
      if (this.camp?.wen) inscriptionDays.push({ field: "wen", label: this.$t("Wednesday").toString() });
      if (this.camp?.thu) inscriptionDays.push({ field: "thu", label: this.$t("Thursday").toString() });
      if (this.camp?.fri) inscriptionDays.push({ field: "fri", label: this.$t("Friday").toString() });
      if (this.camp?.sat) inscriptionDays.push({ field: "sat", label: this.$t("Saturday").toString() });
      if (this.camp?.sun) inscriptionDays.push({ field: "sun", label: this.$t("Sunday").toString() });
    }
    return inscriptionDays;
  }

  private get canSignUp() {
    if (!this.camp) {
      return false;
    }
    if (!this.camp.allowOnlineInscription) {
      return false;
    }
    const now = new Date();
    const start = DateHelper.toDate(this.camp.inscriptionStart, DateHelper.systemFormat)!;
    const end = DateHelper.toDate(this.camp.inscriptionEnd, DateHelper.systemFormat)!;
    return start <= now && now <= end;
  }

  async mounted() {
    await this.loadCamp();
    window.scrollTo({ top: 0 });
  }

  async loadCamp() {
    const cid = CustomerHelper.getStoredCustomer();
    const baseUrl = `${Settings.HostName}/api/publiccamps/${this.campId}`;
    const url = cid !== undefined ? `${baseUrl}/${cid}` : baseUrl;

    let data = await new CrudService().httpGet(url);
    if (data == null) {
      this.error = this.$t("Crash").toString();
      return;
    }

    const camp = data.model as Camp;
    camp.startStr = DateHelper.formatDate(new Date(camp.start), store.state.settings.DateNoTimeFormat);
    camp.endStr = DateHelper.formatDate(new Date(camp.end), store.state.settings.DateNoTimeFormat);
    camp.logoImg = this.getImage(camp.logo);
    this.camp = camp;
    if (data.alreadyIn === true) {
      this.ok = this.$t("Already In!").toString();
    }
  }

  private signUpClicked() {
    if (this.ok !== "") {
      return;
    }
    this.error = "";
    let cid = CustomerHelper.getStoredCustomer();
    if (cid === undefined) {
      this.showLoginRegister = true;
      return;
    }
    this.customerId = cid;
    this.onCustomerSetted();
  }

  private registerDone(data: any) {
    if (data === undefined) {
      return;
    }
    if (data.ok === true) {
      this.customerId = data.id;
      this.tempLogin();
      this.onCustomerSetted();
    }
  }

  private loginDone(data: any) {
    if (data === undefined) {
      return;
    }
    if (data.ok === true) {
      this.customerId = data.id;
      this.onCustomerSetted();
    }
  }

  private onCustomerSetted() {
    this.showLoginRegister = false;
    this.showLogin = false;
    this.showRegister = false;
    this.showInscriptionForm = true;
  }

  private validateInscription(event: MouseEvent) {
    const target = event.target as HTMLButtonElement;
    const form = target.form!;
    const formData = new FormData(form);
    const inscriptionData = Object.fromEntries(
      [...formData.entries()].map(([key, value]) => {
        switch (key) {
          case "mon":
          case "tue":
          case "wen":
          case "thu":
          case "fri":
          case "sat":
          case "sun":
            return [key, value === "on"];
          default:
            return [key, value];
        }
      })
    );
    this.directInscription(inscriptionData, event);
  }

  private async directInscription(formData: Record<string, any>, event: Event) {
    const url = `${Settings.HostName}/api/publiccamps/inscription`;
    let res = await new CrudService().httpPost(url, {
      customerId: this.customerId,
      teamId: this.campId,
      ...formData,
    });
    if (res == null) {
      this.error = this.$t("Crash").toString();
      return;
    }

    if (res.ok === false) {
      this.error = res.errorMsg;
      return;
    }

    const product = res.model.product as Product;
    const extraInfo = res.model.extraInfo as string;
    if (!product) {
      this.ok = this.$t("Sign Up Done!").toString();
    } else {
      type AddProductToCartParams = Parameters<ShopService['addProductToCart']>[3];
      const params = {
        extraInfo,
        parentId: this.campId,
        parentName: ParentNameTeam,
      } as AddProductToCartParams;
      new ShopService().addProductToCart(product, 1, event.target, params);
      this.$router.push("/checkout");
    }

    this.showInscriptionForm = false;
  }

  private tempLogin() {
    let in5Mins: any = DateHelper.addMinutes(new Date(), 5);
    LocalStorage.addWithTimeout(
      "cc",
      JSON.stringify({ id: this.customerId }),
      in5Mins
    );
  }

  private getImage(name: string) {
    if (name !== undefined && name !== "" && name !== null) {
      return store.state.settings.Uploads + "/" + name;
    }
    return store.state.settings.Uploads + "/" + store.state.settings.Mainlogo;
  }

  private async copyLink() {
    this.copyToClipboard(window.location.href);
    this.successMsg = this.$t("Copied").toString();
    this.showPopupCopiedLink = true;
  }

  private copyToClipboard(text) {
    if (!navigator.clipboard) {
      console.log(
        "Yours browser does not allow copy to clipboard" + " " + text
      );
      return;
    }

    navigator.clipboard
      .writeText(text)
      .then(function () {
        console.log("Copied to clipboard");
      })
      .catch(function (err) {
        console.error("Unable to copy", err);
      });
  }
}
